import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-icon';
import { ToastrService } from '../../services/toastr-service';
import { RepairPartService } from '../../services/repair-part.service';
import { CreateRepairPartRequest } from '../../models/create-repair-part-request.model';
import { RepairPartResponse } from '../../models/repair-part-response.model';
import { ErrorComponent } from '../../shared-components/error/error.component';
import { RepairPartFormValue } from '../../form-model/create-repair-part.form.model';
import { RepairPartInformationComponent } from '../../components/create-repair-part/repair-part-information/repair-part-information.component';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';

@Component({
  selector: 'app-create-repair-part',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ErrorComponent,
    NgxSkeletonLoaderModule,
    RepairPartInformationComponent,
    MasterDataAuditLogComponent,
  ],
  templateUrl: './create-repair-part.component.html',
  styleUrls: ['./create-repair-part.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateRepairPartComponent implements OnInit, OnDestroy {
  formSubmitErrors = signal<string[]>([]);
  submitLoader = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  showCancelDialog = signal<boolean>(false);
  showDeleteDialog = signal<boolean>(false);
  deleteLoader = signal<boolean>(false);
  openAuditLog = false;
  repairMode: string | null = null;
  repairCode: string | null = null;
  partCode: string | null = null;
  repairPartData = signal<RepairPartResponse | null>(null);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  createRepairPartForm = this.formBuilder.group({});

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService,
    private readonly repairPartService: RepairPartService,
  ) {}

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    this.repairMode = this.route.snapshot.paramMap.get('repairMode');
    this.repairCode = this.route.snapshot.paramMap.get('repairCode');
    this.partCode = this.route.snapshot.paramMap.get('partCode');

    this.addControlsToTheFormGroup();

    if (this.repairMode && this.repairCode && this.partCode) {
      this.isEditMode.set(true);
      await this.loadRepairPartData(this.repairMode, this.repairCode, this.partCode);
    } else {
      this.isEditMode.set(false);
    }
  }

  private async loadRepairPartData(repairMode: string, repairCode: string, partCode: string): Promise<void> {
    try {
      const data = await firstValueFrom(this.repairPartService.getRepairPartByCompositeKey(repairMode, repairCode, partCode));
      this.repairPartData.set(data);
      this.populateFormWithData(data);
      // Disable non-editable fields in edit mode
      this.createRepairPartForm.get('repairMode')?.disable({ emitEvent: false });
      this.createRepairPartForm.get('repairCode')?.disable({ emitEvent: false });
      this.createRepairPartForm.get('partCode')?.disable({ emitEvent: false });
    } catch (err: any) {
      console.error('Error loading repair part data:', err);
      this.toastrService.showToastr('Failed to load repair part data', 'error');
      this.router.navigate(['/master-data/repair-part']);
    }
  }

  private populateFormWithData(data: RepairPartResponse | null): void {
    if (!data) return;
    this.createRepairPartForm.patchValue({
      repairMode: data.repairMode,
      repairCode: data.repairCode,
      partCode: data.partCode,
      maxQuantity: String(data.maxQuantity),
    });
    this.createRepairPartForm.markAsPristine();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private addControlsToTheFormGroup(): void {
    this.createRepairPartForm.addControl('repairMode', this.formBuilder.nonNullable.control<string>('', [Validators.required]));
    this.createRepairPartForm.addControl('repairCode', this.formBuilder.nonNullable.control<string>('', [Validators.required]));
    this.createRepairPartForm.addControl('partCode', this.formBuilder.nonNullable.control<string>('', [Validators.required]));
    this.createRepairPartForm.addControl('maxQuantity', this.formBuilder.nonNullable.control<string>('', [Validators.required]));
  }

  isControlInErrorState(control: FormControl | AbstractControl | null | undefined): boolean | null {
    return control?.invalid && control?.touched ? true : null;
  }

  onCancel(): void {
    if (this.createRepairPartForm.dirty) {
      this.showCancelDialog.set(true);
    } else {
      this.navigateToRepairPart();
    }
  }

  confirmCancel(): void {
    this.showCancelDialog.set(false);
    this.navigateToRepairPart();
  }

  keepEditing(): void {
    this.showCancelDialog.set(false);
  }

  private navigateToRepairPart(): void {
    this.router.navigate(['/master-data/repair-part']);
  }

  onSubmit(): void {
    if (this.createRepairPartForm.invalid) {
      for (const key of Object.keys(this.createRepairPartForm.controls)) {
        this.createRepairPartForm.get(key)?.markAsTouched();
      }
      this.toastrService.showToastr('Please fill in all required fields correctly', 'warning');
      return;
    }

    if (this.isEditMode()) {
      this.updateRepairPart();
    } else {
      this.createRepairPart();
    }
  }

  private buildCreateRequest(): CreateRepairPartRequest {
    const formValue = this.createRepairPartForm.getRawValue() as RepairPartFormValue;
    return {
      repairMode: formValue.repairMode,
      repairCode: formValue.repairCode,
      partCode: formValue.partCode,
      maxQuantity: Number(formValue.maxQuantity),
    };
  }

  private createRepairPart(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);
    const request = this.buildCreateRequest();

    this.repairPartService
      .createRepairPart(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitLoader.set(false);
          this.createRepairPartForm.reset();
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Repair Code ${request.repairCode} with Part ${request.partCode} added successfully`, 'success');
          this.router.navigate(['/master-data/repair-part']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleRepairPartError(error, 'creating');
        },
      });
  }

  private updateRepairPart(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);

    const formValue = this.createRepairPartForm.getRawValue() as RepairPartFormValue;
    const updatePayload = {
      maxQuantity: Number(formValue.maxQuantity),
    };

    this.repairPartService
      .updateRepairPart(this.repairMode!, this.repairCode!, this.partCode!, updatePayload)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitLoader.set(false);
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Repair Code ${this.repairCode} with Part ${this.partCode} updated successfully`, 'success');
          this.router.navigate(['/master-data/repair-part']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleRepairPartError(error, 'updating');
        },
      });
  }

  onDeleteAssociation(): void {
    this.showDeleteDialog.set(true);
  }

  cancelDeleteAssociation(): void {
    this.showDeleteDialog.set(false);
  }

  confirmDeleteAssociation(): void {
    if (!this.repairMode || !this.repairCode || !this.partCode) {
      this.showDeleteDialog.set(false);
      return;
    }
    this.deleteLoader.set(true);
    this.repairPartService
      .deleteRepairPart(this.repairMode, this.repairCode, this.partCode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.deleteLoader.set(false);
          this.showDeleteDialog.set(false);
          this.toastrService.showToastr(`Repair Code ${this.repairCode} with Part ${this.partCode} deleted successfully`, 'success');
          this.router.navigate(['/master-data/repair-part']);
        },
        error: () => {
          this.deleteLoader.set(false);
          this.showDeleteDialog.set(false);
          this.toastrService.showToastr('Failed to delete association', 'error');
        },
      });
  }

  private handleRepairPartError(error: HttpErrorResponse, operation: string): void {
    this.submitLoader.set(false);
    console.error(`Error ${operation} repair part:`, error);

    if (!error) {
      this.formSubmitErrors.set([`Failed to ${operation} association. Please try again.`]);
      this.toastrService.showToastr(`Failed to ${operation} association. Please try again.`, 'error');
      return;
    }

    if (error.status === 400) {
      this.formSubmitErrors.set(['Invalid data. Please check your inputs.']);
      this.toastrService.showToastr('Invalid data. Please check your inputs.', 'error');
    } else if (error.status === 409) {
      this.formSubmitErrors.set(['This association already exists.']);
      this.toastrService.showToastr('This association already exists.', 'error');
    } else if (error.status === 422 && error.error?.violations) {
      const errors = error.error.violations.map((v: { message: string }) => v.message);
      this.formSubmitErrors.set(errors);
      this.toastrService.showToastr('Validation errors occurred. Please check the form.', 'error');
    } else {
      this.formSubmitErrors.set([`Failed to ${operation} association. Please try again.`]);
      this.toastrService.showToastr(`Failed to ${operation} association. Please try again.`, 'error');
    }
  }

  onAuditLogClick(): void {
    if (!this.repairMode || !this.repairCode || !this.partCode) {
      this.toastrService.showToastr('Repair part keys not available for audit log', 'error');
      return;
    }
    this.openAuditLog = true;
  }

  readonly auditLogFetcher = (_transactionCode: string, isSystemLog: boolean, page: number, limit: number) =>
    this.repairPartService.getRepairPartAuditLogs(this.repairMode!, this.repairCode!, this.partCode!, isSystemLog, page, limit);
}
